/**
 * Route Identification Icons
 *
 * SVG icons for displaying hold colors and tag colors.
 * Each style offers a different visual approach.
 */

import type { CSSProperties } from 'react';

interface IconProps {
  color: string;
  size?: number;
  style?: CSSProperties;
}

// ============================================
// STYLE A: Simple Geometric
// Hold = rounded blob, Tag = flag/pennant
// ============================================

export const HoldIconStyleA = ({ color, size = 24, style }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={style}>
    {/* Organic blob shape representing a climbing hold */}
    <path
      d="M12 4C7 4 4 8 4 12C4 16 7 20 12 20C17 20 20 16 20 12C20 8 17 4 12 4Z"
      fill={color}
      stroke="rgba(0,0,0,0.2)"
      strokeWidth="1"
    />
  </svg>
);

export const TagIconStyleA = ({ color, size = 24, style }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={style}>
    {/* Flag/pennant shape representing tape or tag */}
    <path
      d="M6 4V20M6 4L18 8L6 12"
      fill={color}
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M6 4L18 8L6 12V4Z" fill={color} />
  </svg>
);

// ============================================
// STYLE B: Climbing-Specific
// Hold = jug silhouette, Tag = tape strip
// ============================================

export const HoldIconStyleB = ({ color, size = 24, style }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={style}>
    {/* Jug hold silhouette with finger scoop */}
    <path
      d="M4 10C4 6 8 4 12 4C16 4 20 6 20 10C20 12 18 13 18 15C18 17 16 20 12 20C8 20 6 17 6 15C6 13 4 12 4 10Z"
      fill={color}
      stroke="rgba(0,0,0,0.2)"
      strokeWidth="1"
    />
    {/* Finger scoop indent */}
    <path
      d="M8 11C8 11 10 13 12 13C14 13 16 11 16 11"
      stroke="rgba(0,0,0,0.15)"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

export const TagIconStyleB = ({ color, size = 24, style }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={style}>
    {/* Tape strip with torn edge */}
    <rect x="4" y="7" width="16" height="10" rx="1" fill={color} />
    {/* Torn edge effect */}
    <path
      d="M4 9L5 8L4 7M20 9L19 8L20 7M4 15L5 16L4 17M20 15L19 16L20 17"
      stroke="rgba(255,255,255,0.3)"
      strokeWidth="1"
    />
  </svg>
);

// ============================================
// STYLE C: Minimal Outlined
// Hold = filled circle, Tag = outlined square with dot
// ============================================

export const HoldIconStyleC = ({ color, size = 24, style }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={style}>
    {/* Solid filled circle */}
    <circle cx="12" cy="12" r="8" fill={color} stroke="rgba(0,0,0,0.2)" strokeWidth="1" />
  </svg>
);

export const TagIconStyleC = ({ color, size = 24, style }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={style}>
    {/* Square outline with filled center dot */}
    <rect x="5" y="5" width="14" height="14" rx="2" stroke={color} strokeWidth="2.5" fill="none" />
    <circle cx="12" cy="12" r="3" fill={color} />
  </svg>
);

// ============================================
// STYLE D: Map Pin Style
// Hold = teardrop pin, Tag = flag pin
// ============================================

export const HoldIconStyleD = ({ color, size = 24, style }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={style}>
    {/* Teardrop/map pin shape */}
    <path
      d="M12 2C8 2 5 5 5 9C5 14 12 22 12 22C12 22 19 14 19 9C19 5 16 2 12 2Z"
      fill={color}
      stroke="rgba(0,0,0,0.2)"
      strokeWidth="1"
    />
    <circle cx="12" cy="9" r="3" fill="rgba(255,255,255,0.4)" />
  </svg>
);

export const TagIconStyleD = ({ color, size = 24, style }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={style}>
    {/* Flag on a pin */}
    <path d="M8 22V4" stroke="rgba(0,0,0,0.3)" strokeWidth="2" strokeLinecap="round" />
    <path d="M8 4L20 7L8 12V4Z" fill={color} stroke="rgba(0,0,0,0.2)" strokeWidth="1" />
  </svg>
);

// ============================================
// STYLE E: Badge/Label Style
// Hold = rounded pill, Tag = pointed label
// ============================================

export const HoldIconStyleE = ({ color, size = 24, style }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={style}>
    {/* Rounded pill/capsule */}
    <rect
      x="4"
      y="8"
      width="16"
      height="8"
      rx="4"
      fill={color}
      stroke="rgba(0,0,0,0.2)"
      strokeWidth="1"
    />
  </svg>
);

export const TagIconStyleE = ({ color, size = 24, style }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={style}>
    {/* Price tag / label shape */}
    <path
      d="M4 12L4 6C4 4.9 4.9 4 6 4L12 4L20 12L12 20L4 12Z"
      fill={color}
      stroke="rgba(0,0,0,0.2)"
      strokeWidth="1"
    />
    <circle cx="8" cy="8" r="1.5" fill="rgba(255,255,255,0.5)" />
  </svg>
);

// ============================================
// STYLE F: Hand Crimping Hold (Side Profile)
// Hold = crimp with hand gripping it from side view, Tag = tape strip
// ============================================

export const HoldIconStyleF = ({ color, size = 24, style }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={style}>
    {/* Hand crimping on hold - side profile silhouette */}
    <path
      fill={color}
      d="M1,15 C4,16 8,15 11,13 L16,13 L16,17 L21,17 L21,9 L16,9 L15,9 C17,8 16,6 14,6 C11,6 8,7 5,10 L1,11 Z"
    />
  </svg>
);

export const TagIconStyleF = ({ color, size = 24, style }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={style}>
    {/* Tape strip with angled/torn ends */}
    <path
      d="M3 9L5 7H19L21 9V15L19 17H5L3 15V9Z"
      fill={color}
      stroke="rgba(0,0,0,0.12)"
      strokeWidth="0.5"
    />
    {/* Subtle texture lines */}
    <path
      d="M8 9.5V14.5M12 9.5V14.5M16 9.5V14.5"
      stroke="rgba(255,255,255,0.25)"
      strokeWidth="0.75"
      strokeLinecap="round"
    />
  </svg>
);

// ============================================
// Demo Component to Preview All Styles
// ============================================

export const IconStylePreview = () => {
  const previewColors = {
    hold: '#3b82f6', // Blue
    tag: '#eab308', // Yellow
  };

  const styles = [
    { name: 'Style A: Geometric', Hold: HoldIconStyleA, Tag: TagIconStyleA, desc: 'Blob + Flag' },
    {
      name: 'Style B: Climbing',
      Hold: HoldIconStyleB,
      Tag: TagIconStyleB,
      desc: 'Jug + Tape strip',
    },
    { name: 'Style C: Minimal', Hold: HoldIconStyleC, Tag: TagIconStyleC, desc: 'Circle + Square' },
    {
      name: 'Style D: Map Pins',
      Hold: HoldIconStyleD,
      Tag: TagIconStyleD,
      desc: 'Teardrop + Flag pin',
    },
    { name: 'Style E: Badges', Hold: HoldIconStyleE, Tag: TagIconStyleE, desc: 'Pill + Label tag' },
    {
      name: 'Style F: Realistic Jug',
      Hold: HoldIconStyleF,
      Tag: TagIconStyleF,
      desc: 'Jug w/ scoop + Tape',
    },
  ];

  return (
    <div style={{ padding: 24, fontFamily: 'system-ui' }}>
      <h2 style={{ marginBottom: 24 }}>Route Identification Icon Styles</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {styles.map(({ name, Hold, Tag, desc }) => (
          <div
            key={name}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              padding: 16,
              backgroundColor: '#f5f5f5',
              borderRadius: 8,
            }}
          >
            <div style={{ width: 180 }}>
              <div style={{ fontWeight: 600 }}>{name}</div>
              <div style={{ fontSize: 12, color: '#666' }}>{desc}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Hold color={previewColors.hold} size={32} />
              <span style={{ fontSize: 12, color: '#666' }}>Hold</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tag color={previewColors.tag} size={32} />
              <span style={{ fontSize: 12, color: '#666' }}>Tag</span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 12px',
                backgroundColor: 'white',
                borderRadius: 4,
                marginLeft: 16,
              }}
            >
              <Hold color={previewColors.hold} size={20} />
              <Tag color={previewColors.tag} size={20} />
              <span style={{ fontSize: 14, marginLeft: 8 }}>Combined</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default IconStylePreview;

// ============================================
// Default Exports - Style E (Badges)
// These are the recommended icons for use throughout the app
// ============================================

/** Hold color icon - rounded pill */
export const HoldIcon = HoldIconStyleE;

/** Tag color icon - pointed label */
export const TagIcon = TagIconStyleE;
